import { Button, Chip, Stack, Typography } from "@mui/material";
import type { MouseEvent } from "react";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import UserAvatar from "@entrilia/shared/components/UserAvatar";
import type { VersionId } from "./versionHistory.types";

interface Props {
  title: string;
  id: VersionId;
  authorName?: string;
  versionNumber: number;
  isUnsavedVersion?: boolean;
  currentVersionId: VersionId | undefined;
  selectedVersionId: VersionId | undefined;
  previewVersionId: VersionId | undefined;
  onPreview: () => void;
}

const VersionHistoryItem = ({
  title,
  id,
  authorName,
  versionNumber,
  currentVersionId,
  selectedVersionId,
  isUnsavedVersion,
  previewVersionId,
  onPreview,
}: Props) => {
  const handlePreviewClick = (event: MouseEvent) => {
    event.stopPropagation();
    onPreview();
  };
  const isCurrent = currentVersionId === id;
  const isSelected = selectedVersionId === id;
  const isPreview = previewVersionId === id;

  const showPreviewButton = previewVersionId === undefined ? !isCurrent : !isPreview;

  return (
    <Stack
      sx={(theme) => ({
        px: 1.5,
        py: 1,
        borderRadius: 1,
        border: 1,
        borderColor: isSelected ? "primary.main" : "divider",
        transition: theme.transitions.create(["background-color", "border-color"]),
        "&:hover": {
          backgroundColor: "action.hover",
        },
        minHeight: isUnsavedVersion ? 50 : 72,
      })}
    >
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", gap: 1, minHeight: 32 }}>
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {title}
        </Typography>
        {isCurrent && <Chip label="Current" color="success" variant="outlined" />}
        {showPreviewButton && (
          <Button variant="outlined" color="secondary" onClick={handlePreviewClick}>
            Preview
          </Button>
        )}
      </Stack>
      {!isUnsavedVersion && authorName && (
        <Stack direction="row" sx={{ alignItems: "center", gap: 1, mt: 0.5, minHeight: 32 }}>
          <UserAvatar userName={authorName} size={20} />
          <TypographyTooltipEllipsis
            text={authorName}
            typographyProps={{ variant: "caption", color: "textSecondary", sx: { flex: 1 } }}
            tooltipProps={{ disableInteractive: true }}
          />
          {versionNumber > 0 && (
            <Typography variant="caption" noWrap color="textSecondary" sx={{ ml: "auto" }}>
              Version #{versionNumber}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default VersionHistoryItem;
